/**
 * Parse/format timestamps from usage & billing APIs.
 * Backend values are UTC; naive ISO strings (no Z/offset) must not be treated as local time.
 */

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;
const HAS_TIMEZONE = /(?:[zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/;

const USAGE_CREATED_AT_KEYS = [
  'created_at',
  'started_at',
  'start_time',
  'timestamp',
  'run_created_at',
  'run_started_at',
] as const;

const USAGE_COMPLETED_AT_KEYS = [
  'completed_at',
  'finished_at',
  'ended_at',
  'completed_time',
  'end_time',
  'updated_at',
  'run_completed_at',
  'run_finished_at',
] as const;

const USAGE_TIMELINE_NESTED_KEYS = ['run', 'entity', 'metadata'] as const;

export function normalizeApiDateTimeString(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (HAS_TIMEZONE.test(trimmed)) return trimmed;
  if (ISO_DATE_ONLY.test(trimmed)) return `${trimmed}T00:00:00.000Z`;
  if (ISO_DATE_TIME.test(trimmed)) return `${trimmed}Z`;
  return trimmed;
}

export function parseApiDateTime(raw: unknown): Date | null {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    const ms = n < 1e12 ? n * 1000 : n;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(normalizeApiDateTimeString(trimmed));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatUsageDateTime(raw: unknown): string {
  const date = parseApiDateTime(raw);
  if (!date) return typeof raw === 'string' && raw.trim() ? raw : '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function resolveUsageDurationMs(
  input: UsageTimelineInput | Record<string, unknown>,
  fallback?: UsageTimelineInput | Record<string, unknown>
): number | null {
  const primary = normalizeUsageTimelineInput(input);
  const fb = fallback ? normalizeUsageTimelineInput(fallback) : null;
  const explicit = readDurationMs(primary, fb ?? undefined);
  if (explicit != null) return explicit;

  const { created, completed } = resolveUsageTimeline(input, fallback);
  if (!created || !completed) return null;
  const diff = completed.getTime() - created.getTime();
  return diff >= 0 ? diff : null;
}

/** Human-readable duration for Usage detail panels. */
export function formatUsageDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;

  const totalSeconds = Math.floor(ms / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  if (days > 0) {
    const parts: string[] = [`${days}d`];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  if (totalHours > 0) {
    const parts: string[] = [`${totalHours}h`];
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  if (totalMinutes > 0) {
    return seconds > 0 ? `${totalMinutes}m ${seconds}s` : `${totalMinutes}m`;
  }

  return `${totalSeconds}s`;
}

export interface UsageTimelineInput {
  created_at?: string | null;
  completed_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  updated_at?: string | null;
  duration_ms?: number | null;
  status?: string | null;
}

function isPresentTimelineValue(value: unknown): value is string | number {
  if (value == null || value === '') return false;
  return typeof value === 'string' || typeof value === 'number';
}

function flattenUsageTimelineRecord(input: Record<string, unknown>): Record<string, unknown> {
  const flat: Record<string, unknown> = { ...input };
  for (const key of USAGE_TIMELINE_NESTED_KEYS) {
    const nested = input[key];
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) continue;
    Object.assign(flat, nested as Record<string, unknown>);
  }
  return flat;
}

function pickTimelineValue(
  input: Record<string, unknown>,
  keys: readonly string[]
): string | number | null {
  const record = flattenUsageTimelineRecord(input);
  for (const key of keys) {
    const value = record[key];
    if (isPresentTimelineValue(value)) return value;
  }
  return null;
}

function coalesceTimelineString(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (value == null || value === '') continue;
    if (parseApiDateTime(value)) return value;
  }
  for (const value of values) {
    if (value == null || value === '') continue;
    return value;
  }
  return null;
}

/** Map usage list/detail payloads to canonical timeline fields. */
export function normalizeUsageTimelineInput(
  input: UsageTimelineInput | Record<string, unknown> | null | undefined
): UsageTimelineInput {
  if (!input || typeof input !== 'object') return {};
  const record = input as Record<string, unknown>;
  const created = pickTimelineValue(record, USAGE_CREATED_AT_KEYS);
  const completed = pickTimelineValue(record, USAGE_COMPLETED_AT_KEYS);
  const duration =
    typeof record.duration_ms === 'number' && Number.isFinite(record.duration_ms)
      ? record.duration_ms
      : null;
  const status = typeof record.status === 'string' ? record.status : null;

  return {
    created_at: created != null ? String(created) : null,
    completed_at: completed != null ? String(completed) : null,
    duration_ms: duration,
    status,
  };
}

function isTerminalSuccessStatus(status: string | null | undefined): boolean {
  const normalized = (status ?? '').trim().toLowerCase();
  return (
    normalized === 'succeeded' ||
    normalized === 'completed' ||
    normalized === 'complete' ||
    normalized === 'success'
  );
}

function readDurationMs(...sources: Array<UsageTimelineInput | undefined>): number | null {
  for (const source of sources) {
    if (!source) continue;
    const value = source.duration_ms;
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value;
  }
  return null;
}

function pickResolvedCreated(...sources: Array<UsageTimelineInput | Record<string, unknown> | undefined>): Date | null {
  for (const source of sources) {
    if (!source) continue;
    const normalized = normalizeUsageTimelineInput(source);
    const date = parseApiDateTime(normalized.created_at);
    if (date) return date;
  }
  return null;
}

function pickResolvedCompleted(
  ...sources: Array<UsageTimelineInput | Record<string, unknown> | undefined>
): Date | null {
  for (const source of sources) {
    if (!source) continue;
    const normalized = normalizeUsageTimelineInput(source);
    const date = parseApiDateTime(normalized.completed_at);
    if (date) return date;
  }
  return null;
}

/** Ensure Created/Completed display preserves chronology for usage detail drawers. */
export function resolveUsageTimeline(
  input: UsageTimelineInput | Record<string, unknown>,
  fallback?: UsageTimelineInput | Record<string, unknown>
): {
  created: Date | null;
  completed: Date | null;
} {
  const primary = normalizeUsageTimelineInput(input);
  const fb = fallback ? normalizeUsageTimelineInput(fallback) : null;

  let created = pickResolvedCreated(input, fallback);
  let completed = pickResolvedCompleted(input, fallback);

  const durationMs = readDurationMs(primary, fb ?? undefined);
  const status = primary.status ?? fb?.status ?? null;

  if (!completed && created && durationMs != null) {
    completed = new Date(created.getTime() + durationMs);
  }

  if (
    created &&
    completed &&
    completed.getTime() < created.getTime() &&
    isTerminalSuccessStatus(status)
  ) {
    const altCreated = pickResolvedCompleted(input, fallback);
    const altCompleted = pickResolvedCreated(input, fallback);
    if (altCreated && altCompleted && altCompleted.getTime() >= altCreated.getTime()) {
      created = altCreated;
      completed = altCompleted;
    }
  }

  return { created, completed };
}

export function hasResolvableUsageTimelineCreated(
  input: UsageTimelineInput | Record<string, unknown>,
  fallback?: UsageTimelineInput | Record<string, unknown>
): boolean {
  return resolveUsageTimeline(input, fallback).created != null;
}

export function hasResolvableUsageTimelineCompleted(
  input: UsageTimelineInput | Record<string, unknown>,
  fallback?: UsageTimelineInput | Record<string, unknown>
): boolean {
  return resolveUsageTimeline(input, fallback).completed != null;
}

export function formatUsageTimelineCreated(
  input: UsageTimelineInput | Record<string, unknown>,
  fallback?: UsageTimelineInput | Record<string, unknown>
): string {
  const { created } = resolveUsageTimeline(input, fallback);
  return created ? formatUsageDateTime(created) : '—';
}

export function formatUsageTimelineCompleted(
  input: UsageTimelineInput | Record<string, unknown>,
  fallback?: UsageTimelineInput | Record<string, unknown>
): string {
  const { completed } = resolveUsageTimeline(input, fallback);
  return completed ? formatUsageDateTime(completed) : '—';
}

function coalesceUsageTimelineFields<T extends UsageTimelineInput>(record: T): T {
  const normalized = normalizeUsageTimelineInput(record);
  return {
    ...record,
    created_at: coalesceTimelineString(record.created_at, record.started_at, normalized.created_at),
    started_at: coalesceTimelineString(record.started_at, record.created_at, normalized.created_at),
    completed_at: coalesceTimelineString(
      record.completed_at,
      record.finished_at,
      record.updated_at,
      normalized.completed_at
    ),
    finished_at: coalesceTimelineString(
      record.finished_at,
      record.completed_at,
      record.updated_at,
      normalized.completed_at
    ),
    updated_at: coalesceTimelineString(record.updated_at, record.completed_at, normalized.completed_at),
    duration_ms: record.duration_ms ?? normalized.duration_ms ?? null,
    status: record.status || normalized.status || null,
  };
}

/** Fill sparse usage detail payloads; detail API wins over list row for timeline fields. */
export function mergeUsageDetailWithListItem<T extends UsageTimelineInput>(
  detail: T,
  listItem?: UsageTimelineInput | null
): T {
  if (!listItem) return coalesceUsageTimelineFields(detail);
  return coalesceUsageTimelineFields({
    ...detail,
    created_at: coalesceTimelineString(
      detail.created_at,
      detail.started_at,
      listItem.created_at,
      listItem.started_at
    ),
    started_at: coalesceTimelineString(
      detail.started_at,
      detail.created_at,
      listItem.started_at,
      listItem.created_at
    ),
    completed_at: coalesceTimelineString(
      detail.completed_at,
      detail.finished_at,
      detail.updated_at,
      listItem.completed_at,
      listItem.finished_at,
      listItem.updated_at
    ),
    finished_at: coalesceTimelineString(
      detail.finished_at,
      detail.completed_at,
      listItem.finished_at,
      listItem.completed_at,
      listItem.updated_at
    ),
    updated_at: coalesceTimelineString(detail.updated_at, detail.completed_at, listItem.updated_at, listItem.completed_at),
    duration_ms: detail.duration_ms ?? listItem.duration_ms ?? null,
    status: detail.status || listItem.status || null,
  });
}
